/* eslint-disable max-len */
import React, { useEffect } from 'react';
import { extend } from '@thd-nucleus/data-sources';
import PropTypes from 'prop-types';
import { useImpression } from '@thd-olt-component-react/impression';
import { Card, CardBody } from '@one-thd/sui-atomic-components';
import { ListSettings, EmailList } from '@thd-olt-component-react/list-forms';
import ListHeader from './sub-components/ListHeader';
import ListImages from './sub-components/ListImages';
import ListTilePlaceholder from './ListTilePlaceholder';

const { func, shape, string, arrayOf, number, bool } = PropTypes;

export const ListTile = ({
  listId,
  data,
  onDelete,
  position,
  iconSize,
  disableLink = false,
  disableIconAction = false,
  numberOfThumbnails = 2,
  enableSharedIcon = false,
  hideSubHeader = false,
  maxWidth,
  disableThumbnailsLink = false,
  viewMode = 'grid'
}) => {

  const list = data;
  const isEmptyList = list?.itemCount === 0;
  const shouldGrowBody = viewMode === 'thumbnails' && isEmptyList;

  const maxWidthClass = {
    'xs': 'sui-max-w-xs',
    'sm': 'sui-max-w-sm', 
    'md': 'sui-max-w-md',
    'lg': 'sui-max-w-lg',
    'xl': 'sui-max-w-xl',
    '2xl': 'sui-max-w-2xl'
  }[maxWidth] || '';

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('list-tile.ready');
  }, []);

  const onDeleteConfirmation = () => {
    if (onDelete) {
      onDelete({ listId });
    }
  };

  const handleEmptyListNavigation = () => {
    if (list?.url) {
      window.location.href = list.url;
    }
  };

  const impressionContextData = useImpression({
    data: {
      id: listId,
      name: 'ListTile',
      component: 'ListTile',
      position: position + 1,
      type: 'content'
    }
  });

  if (!list) return <ListTilePlaceholder numberOfThumbnails={numberOfThumbnails} />;

  if (!list || !list?.url) return null;

  const listTileRef = impressionContextData.ref;
  const listTileClickId = impressionContextData.clickID;

  const actionMenuButton = (
    <>
      {
        disableIconAction ? null : enableSharedIcon ? (
          list?.itemCount > 0 ? (
            <div className="sui-p-2 hover:sui-bg-subtle sui-rounded-base sui-cursor-pointer">
              <EmailList listId={list.id} iconSize={iconSize} shareIcon />
            </div>
          ) : null
        ) : (
          <ListSettings
            iconSize={iconSize}
            onClose={() => { }}
            list={list}
            onDelete={onDeleteConfirmation}
            isEmptyList={isEmptyList}
          />
        )
      }
    </>
  );

  return (
    <div
      className="sui-w-full sui-h-full"
      ref={listTileRef}
      // eslint-disable-next-line react/no-unknown-property
      clickid={listTileClickId}
      data-component="ListTile"
      data-testid="list-tile"
    >
      <Card
        className={`sui-w-full sui-h-full [&>div]:sui-h-full ${maxWidthClass} [&>div]:sui-justify-between`}
      >
        <div className="sui-flex sui-justify-between sui-items-start sui-gap-2">
          <ListHeader
            name={list?.name} 
            url={list?.url} 
            itemCount={list?.itemCount} 
            hideSubHeader={hideSubHeader}
            disableLink={disableLink}
            viewMode={viewMode}
          />
          {actionMenuButton}
        </div>
        <CardBody disableGrow={!shouldGrowBody}>
          <ListImages
            images={list?.images}
            itemCount={list?.itemCount}
            numberOfThumbnails={numberOfThumbnails}
            url={list?.url}
            disableThumbnailsLink={disableThumbnailsLink}
            handleEmptyListNavigation={handleEmptyListNavigation}
            viewMode={viewMode}
          />
        </CardBody>
      </Card>
    </div>
  );
};

ListTile.displayName = 'ListTile';

ListTile.defaultProps = {
  onDelete: null,
  data: null,
  position: 0,
  iconSize: 'small',
  disableLink: false,
  disableIconAction: false,
  numberOfThumbnails: 2,
  enableSharedIcon: false,
  hideSubHeader: false,
  maxWidth: null,
  disableThumbnailsLink: false,
  viewMode: 'grid'
};

ListTile.propTypes = {
  listId: string.isRequired,
  onDelete: func,
  iconSize: string,
  data: shape({
    id: string,
    name: string,
    url: string,
    itemCount: number,
    images: arrayOf(shape({
      url: string
    }))
  }),
  position: number,
  disableLink: bool,
  disableIconAction: bool,
  numberOfThumbnails: number,
  enableSharedIcon: bool,
  hideSubHeader: bool,
  maxWidth: string,
  disableThumbnailsLink: bool,
  viewMode: string
};

ListTile.dataModel = extend(ListSettings);
